/**
 * Schedule-event detail (WO-22) — pushed OVER the tabs (title "Event", with back).
 *
 * Shows the event's fields and supports edit (reschedule = change starts_at/ends_at,
 * reassign, type/priority/flexibility) via an inline ScheduleEventForm, plus delete.
 * On delete, navigates back to the Calendar tab. NEVER imports @smartbox/*. No raw styles.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getScheduleEvent,
  updateScheduleEvent,
  deleteScheduleEvent,
} from '../../../src/api/calendar';
import {
  Screen,
  Text,
  Card,
  Button,
  Badge,
  priorityBadge,
} from '../../../src/components/ui';
import { ScheduleEventForm } from '../../../src/components/schedule/ScheduleEventForm';
import { scheduleEventMeta } from '../../../src/lib/scheduleMeta';
import { formatDateTime, extractErrorMessage } from '../../../src/lib/format';
import { theme } from '../../../src/theme';
import type { UpdateScheduleEventRequest } from '@beam/api-types';

export default function ScheduleEventDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['schedule-events', id],
    queryFn: () => getScheduleEvent(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateScheduleEventRequest) => updateScheduleEvent(id!, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(['schedule-events', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
      setEditing(false);
      setActionError(null);
    },
    onError: (err) => setActionError(extractErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteScheduleEvent(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
      router.navigate('/(app)/calendar');
    },
    onError: (err) => setActionError(extractErrorMessage(err)),
  });

  if (!id) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No event ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading event"
          />
        </View>
      </Screen>
    );
  }

  if (isError || !data) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error, 'Failed to load event.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
            />
          </Card>
        </View>
      </Screen>
    );
  }

  const meta = scheduleEventMeta(data.event_type);
  const pri = priorityBadge(data.priority);

  if (editing) {
    return (
      <Screen scroll>
        <ScheduleEventForm
          initial={data}
          busy={updateMutation.isPending}
          submitLabel="Save changes"
          onCancel={() => setEditing(false)}
          onSubmit={(v) =>
            updateMutation.mutate({
              event_type: v.event_type,
              title: v.title,
              description: v.description,
              starts_at: v.starts_at,
              ends_at: v.ends_at,
              all_day: v.all_day,
              is_flexible: v.is_flexible,
              priority: v.priority,
              assigned_to: v.assigned_to,
            })
          }
        />
        {actionError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {actionError}
            </Text>
          </Card>
        ) : null}
      </Screen>
    );
  }

  return (
    <Screen scroll>
      <View style={styles.statusRow}>
        <Text variant="title">{data.title}</Text>
        <Badge status={pri.key} label={pri.label} />
      </View>

      <Card>
        <Row label="Type" value={meta.label} />
        <Row label="Starts" value={data.all_day ? 'All day' : formatDateTime(data.starts_at)} />
        <Row label="Ends" value={data.all_day ? 'All day' : formatDateTime(data.ends_at)} />
        <Row label="Scheduling" value={data.is_flexible ? 'Flexible' : 'Fixed'} />
        {data.assigned_to ? (
          <Row label="Assigned" value={data.assigned_to.slice(0, 8).toUpperCase()} />
        ) : null}
        {data.job_id ? (
          <Row label="Job" value={data.job_id.slice(0, 8).toUpperCase()} />
        ) : null}
      </Card>

      {actionError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {actionError}
          </Text>
        </Card>
      ) : null}

      <View style={styles.actions}>
        <Button title="Edit / Reschedule" onPress={() => setEditing(true)} />
        <Button
          title="Delete event"
          variant="danger"
          loading={deleteMutation.isPending}
          disabled={deleteMutation.isPending}
          onPress={() => deleteMutation.mutate()}
        />
      </View>
    </Screen>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.cardRow}>
      <Text variant="small" color="secondary">
        {label}
      </Text>
      <Text variant="data">{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actions: { gap: theme.spacing.sm },
});
